import { useCallback, useLayoutEffect, useRef, useState } from 'react';
import {
  CHIP_GAP_PX,
  getLabelLayout,
  MAX_LABEL_LENGTH,
} from './drive-label-chips-cell.utils';

import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import type { ChipProps } from '@mui/material/Chip';
import type { TooltipProps } from '@mui/material/Tooltip';

export interface DriveLabelBadgeOption {
  readonly id: string;
  readonly text: string;
  readonly disabled?: boolean;
  readonly backgroundColor?: string;
  readonly foregroundColor?: string;
}

export interface DriveLabelChip {
  readonly id: string;
  readonly title: string;
  readonly labelTitle: string;
  readonly backgroundColor?: string;
  readonly foregroundColor?: string;
  readonly allBadges?: DriveLabelBadgeOption[];
}

interface DriveLabelChipsCellProps {
  readonly labels: DriveLabelChip[];
}

interface LabelLayout {
  readonly visibleCount: number;
  readonly truncatedLabelMaxWidth?: number;
}

const fullLabelChipSx = {
  maxWidth: `${MAX_LABEL_LENGTH}ch`,
  flexShrink: 0,
} as const;

const truncatableLabelChipSx = {
  minWidth: 0,
  flexShrink: 0,
  maxWidth: `min(100%, ${MAX_LABEL_LENGTH}ch)`,
  '& .MuiChip-label': {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
} as const;

const countChipSx = {
  flexShrink: 0,
} as const;

function coloredChipSx(backgroundColor?: string, foregroundColor?: string) {
  return backgroundColor
    ? { bgcolor: backgroundColor, color: foregroundColor ?? 'common.white' }
    : undefined;
}

export default function DriveLabelChipsCell({
  labels,
}: DriveLabelChipsCellProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [layout, setLayout] = useState<LabelLayout>({
    visibleCount: labels.length,
  });

  const titles = labels.map((label) => label.title);
  // Stable, content-based proxy for `titles` (which is a new array reference
  // every render) so `updateLayout`'s memoization isn't defeated on every render.
  const titlesKey = JSON.stringify(titles);

  const updateLayout = useCallback(() => {
    const container = containerRef.current;
    const measure = measureRef.current;
    if (!container || !measure || !titles.length) {
      return;
    }

    const containerWidth = container.clientWidth;
    if (containerWidth <= 0) {
      return;
    }

    const chipWidths = Array.from(
      measure.querySelectorAll('[data-label-chip]')
    ).map((element) => element.getBoundingClientRect().width);

    const countChipWidths = new Map<number, number>();
    measure.querySelectorAll('[data-count-chip]').forEach((element) => {
      const count = Number(element.getAttribute('data-count'));
      if (!Number.isNaN(count)) {
        countChipWidths.set(count, element.getBoundingClientRect().width);
      }
    });

    setLayout((prev) => {
      const next = getLabelLayout(
        chipWidths,
        containerWidth,
        countChipWidths,
        CHIP_GAP_PX
      );

      if (
        prev.visibleCount === next.visibleCount &&
        prev.truncatedLabelMaxWidth === next.truncatedLabelMaxWidth
      ) {
        return prev;
      }

      return next;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- titlesKey is a stable proxy for titles' content
  }, [titlesKey]);

  useLayoutEffect(() => {
    updateLayout();

    const container = containerRef.current;
    if (!container) {
      return;
    }

    // Recalculate when the grid column is resized.
    const resizeObserver = new ResizeObserver(updateLayout);
    resizeObserver.observe(container);

    return () => {
      resizeObserver.disconnect();
    };
  }, [updateLayout]);

  if (!labels.length) {
    return <Typography>-</Typography>;
  }

  const { visibleCount, truncatedLabelMaxWidth } = layout;
  const hiddenCount = labels.length - visibleCount;
  const hasOverflow = hiddenCount > 0;
  // Only a single visible label ellipsizes; multiple visible labels stay full width.
  const useTruncatableLabel = visibleCount === 1;

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        minWidth: 0,
      }}
    >
      <Box
        ref={measureRef}
        aria-hidden
        sx={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          height: 0,
          overflow: 'hidden',
          display: 'flex',
          flexDirection: 'row',
          gap: 1,
        }}
      >
        {labels.map((label) => (
          <Chip
            key={`measure-${label.id}`}
            data-label-chip
            label={label.title}
            size="small"
            sx={fullLabelChipSx}
          />
        ))}
        {Array.from({ length: Math.max(0, titles.length - 1) }, (_, index) => {
          const count = index + 1;
          return (
            <Chip
              key={`measure-count-${count}`}
              data-count-chip
              data-count={count}
              label={`+${count}`}
              size="small"
              sx={countChipSx}
            />
          );
        })}
      </Box>

      <Stack
        ref={containerRef}
        flexDirection="row"
        alignItems="center"
        gap={1}
        height="100%"
        width="100%"
        sx={{ minWidth: 0, overflow: 'hidden' }}
      >
        {labels.slice(0, visibleCount).map((label) => (
          <LabelChip
            key={label.id}
            label={label}
            placement="top-start"
            sx={
              useTruncatableLabel
                ? {
                    ...truncatableLabelChipSx,
                    ...(hasOverflow && truncatedLabelMaxWidth !== undefined
                      ? { maxWidth: truncatedLabelMaxWidth }
                      : {}),
                  }
                : fullLabelChipSx
            }
          />
        ))}
        {hasOverflow && (
          <Tooltip
            title={
              <Stack gap={0.5} flexDirection="row" flexWrap="wrap">
                {labels.slice(visibleCount).map((label) => (
                  <LabelChip key={label.id} label={label} placement="bottom" />
                ))}
              </Stack>
            }
            placement="top-start"
            arrow
            slotProps={tooltipSlotProps}
          >
            <Chip label={`+${hiddenCount}`} size="small" sx={countChipSx} />
          </Tooltip>
        )}
      </Stack>
    </Box>
  );
}

const tooltipSlotProps = {
  tooltip: {
    sx: {
      maxWidth: 550,
      bgcolor: 'background.paper',
      color: 'text.primary',
      boxShadow: 1,
      p: 1,
    },
  },
  arrow: {
    sx: {
      color: 'background.paper',
    },
  },
} as const;

function LabelChip({
  label,
  placement,
  sx,
}: {
  label: DriveLabelChip;
  placement: TooltipProps['placement'];
  sx?: ChipProps['sx'];
}) {
  const chip = (
    <Chip
      label={label.title}
      size="small"
      sx={{
        ...sx,
        ...coloredChipSx(label.backgroundColor, label.foregroundColor),
      }}
    />
  );

  if (!label.allBadges?.length) {
    return chip;
  }

  return (
    <Tooltip
      title={
        <LabelTooltipContent
          labelTitle={label.labelTitle}
          badges={label.allBadges}
        />
      }
      placement={placement}
      arrow
      slotProps={tooltipSlotProps}
    >
      {chip}
    </Tooltip>
  );
}

function BadgeChip({ badge }: { badge: DriveLabelBadgeOption }) {
  const chip = (
    <Chip
      label={badge.text}
      size="small"
      sx={{
        opacity: badge.disabled ? 0.5 : 1,
        ...coloredChipSx(badge.backgroundColor, badge.foregroundColor),
      }}
    />
  );

  if (!badge.disabled) {
    return chip;
  }

  return (
    <Tooltip title="Disabled" arrow placement="top">
      {chip}
    </Tooltip>
  );
}

function LabelTooltipContent({
  labelTitle,
  badges,
}: {
  labelTitle: string;
  badges?: DriveLabelBadgeOption[];
}) {
  return (
    <Stack gap={0.5}>
      <Typography variant="body2" sx={{ fontWeight: 600 }}>
        {labelTitle}
      </Typography>
      {badges && badges.length > 0 && (
        <Stack gap={0.5} flexDirection="row" flexWrap="wrap">
          {badges.map((badge) => (
            <BadgeChip key={badge.id} badge={badge} />
          ))}
        </Stack>
      )}
    </Stack>
  );
}
